import type { AxiosResponse } from "axios"
import type { TDBItem } from "@/types"
import { type generateAPIResource, queryCache } from "@/utils"

export type TAPIResource<T extends TDBItem> = ReturnType<typeof generateAPIResource<T>>

export function resourceInitialData<T extends TDBItem>(url: string, id?: string) {
  return () => {
    const queries = queryCache.findAll({ queryKey: [url] }).filter((q) => q.meta?.list)
    for (const query of queries) {
      if (query.meta?.resource) continue
      const data = query.state.data as AxiosResponse<T[]>
      if (data) {
        const item = data.data.find((item) => item.id == id)
        if (item) {
          return item
        }
      }
    }
  }
}

export const updateQueries = (url, updater) =>
  queryCache.findAll({ queryKey: [url] }).forEach((query) => {
    query.setData(updater(query.state.data, query))
  })

export const createResource =
  <T extends TDBItem>(resource: T) =>
  (oldData: any, query) =>
    query?.meta?.list
      ? { ...oldData, data: [...oldData.data, resource] }
      : Array.isArray(oldData)
        ? [...oldData, resource]
        : resource

export const updateResource =
  <T extends TDBItem>(data: T) =>
  (oldData: any, query) => {
    if (oldData) {
      if (query?.meta?.list)
        return {
          ...oldData,
          data: oldData.data.map((item) => (item.id == data.id ? { ...item, ...data } : item)),
        }

      if (query?.meta?.infinite && Array.isArray(oldData.pages)) {
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: page.data.map((item: any) => (item.id == data.id ? { ...item, ...data } : item)),
          })),
        }
      }

      if (Array.isArray(oldData) && oldData.some((item) => item.id == data.id)) {
        return oldData.map((item) => (item.id == data.id ? { ...item, ...data } : item))
      }

      if (oldData.id == data.id) {
        return { ...oldData, ...data }
      }
    }
    return oldData
  }

export const removeResource = (id: string) => (oldData: any, query) => {
  if (query?.meta?.infinite && Array.isArray(oldData?.pages)) {
    return {
      ...oldData,
      pages: oldData.pages.map((page: any) => ({
        ...page,
        data: page.data.filter((item: any) => item.id != id),
      })),
    }
  }

  return query?.meta?.list
    ? {
        ...oldData,
        data: oldData.data.filter((item) => item.id != id),
      }
    : Array.isArray(oldData)
      ? oldData.filter((item) => item.id != id)
      : null
}
